// confirm-view.ts — the in-game "Are you sure?" confirmation DOM overlay (HTML/DOM
// UI shell plan, Phase 5, build/order/recruit/place STEP 1). Renders the canvas
// kidfAreYouSure DialogForm (1001) faithfully — and the canvas DialogForm is NOT a
// ShellForm, so (per the original):
//   - it does NOT zip in (only ShellForm.ShellDoModal zips; DialogForm.DoModal does not);
//   - it is positioned OVER THE GOB — centered on the structure's screen centre and
//     clamped to the playfield (buildui.ts PopupConfirmation, NOT screen-centred);
//   - it has the DialogForm chrome (form.ts:1022-1068): a black BORDER, a TITLE BAR of
//     height kcyTitle=10 filled with the side colour (SetTitleColor(GetSideColor(side))),
//     and a TRANSLUCENT body (SetBackgroundColorIndex(kiclrShadow) ⇒ ShadowHelper — the
//     gob shows through). The title text (kidcTitle, kifntTitle) is white ON the colour
//     bar; "Are you sure?" (kifntShadow) + Yes/No sit in the translucent body.
//
// COMMAND-ADJACENT, DISPLAY-ONLY: this view emits NO command and constructs NO Message.
// It reports the choice through onResult(confirmed); BuildUI.PopupConfirmation routes it
// to the SAME continuation the canvas uses (YES → the existing cmdq.Enqueue; NO →
// nothing).
//
// forms.ini.pp [kidfAreYouSure] (FORM 0 0 73 45, default control kidcCancel):
//   LABEL=kidcTitle (0 2 73 0) "SELL BUILDING" kifntTitle center   ← runtime title
//   LABEL=0         (12 15 55 0) "Are you sure?" kifntShadow center ← static
//   BUTTON=kidcOk     (4 29 29 12) "Yes" kifntButton
//   BUTTON=kidcCancel (42 29 27 12) "No"  kifntButton

import type { ButtonArt } from '../title-art.ts';
import {
  COL_FG,
  TEXT_SHADOW,
  el,
  FONT_BODY,
  buttonWidthU,
  formScale,
  at,
  formTitle,
  makeStageButton,
  type StageButtonSize,
  rootBox,
} from '../chrome.ts';

// forms.ini.pp kidfAreYouSure 1001 (FORM 0 0 73 45), in design units.
const FORM = { w: 73, h: 45 };
const TITLE = { x: 0, y: 2, w: 73 };
const PROMPT = { x: 12, y: 15, w: 55 };
const YES = { x: 4, y: 29, w: 29, h: 12 };
const NO = { x: 42, y: 29, w: 27, h: 12 };
// form.ts: kcyTitle=10 (the title-bar height); gcxyBorder ≈ 1 design unit (the black frame).
const CY_TITLE = 10;
const BORDER = 1;

// the WI faces (out/fontid): body=Eurostile (kifntShadow prompt), title=Quadtrangle
// (kidcTitle kifntTitle), buttons=Eurostile (Yes/No kifntButton). FONT = body default.
const FONT = FONT_BODY;
const shadow = TEXT_SHADOW;

export interface ConfirmViewDeps {
  /** the overlay container (created by the host; this view owns its contents). */
  root: HTMLElement;
  /** the runtime title (kidcTitle), e.g. "SELL BUILDING" / "ABORT BUILD". */
  title: string;
  /** the TITLE-BAR colour — the canvas SetTitleColor(GetSideColor(gob side)) fill
   *  (form.ts:1067). A CSS color string; the title TEXT stays white over it. */
  titleColor?: string;
  /** the gob's screen CENTRE in game (back-buffer) px — the canvas centres the dialog
   *  here (PcFromWc(gobCenter - viewPos)) and clamps to the playfield. null ⇒ screen
   *  centre (the forceShow preview path). */
  anchor?: { cx: number; cy: number } | null;
  /** the canvas magnification (kGameScale) — game px × this = CSS px, so the anchor
   *  (game px) lands at the gob's on-screen position. */
  magnify?: number;
  /** the raw 9-slice button pieces — faithful chrome; absent ⇒ a CSS-button fallback. */
  buttonArt?: ButtonArt;
  /** play a GUI sfx (ksfxGui*) on a button press, wired to the host SoundMgr. */
  playSfx?: (sfx: number) => void;
  /** the user's choice → the host unmounts the overlay and routes this to the SAME
   *  continuation the canvas DoModal uses (YES runs the existing command action; NO
   *  does nothing). YES = kidcOk; NO / backdrop / Esc = kidcCancel (the FORM default). */
  onResult: (confirmed: boolean) => void;
}

export class ConfirmView {
  private readonly d: ConfirmViewDeps;
  /** the shared 9-slice button renderer (the small dialog fallback flavour). */
  private readonly btn: (label: string, on: () => void, size: StageButtonSize) => HTMLButtonElement;
  /** guards against a double-result (a click + an Esc landing together). */
  private resolved = false;

  constructor(d: ConfirmViewDeps) {
    this.d = d;
    const mk = makeStageButton(d);
    this.btn = (label, on, size) => mk(label, on, size, { fallback: 'dialog' });
    const s = d.root.style;
    // a fixed, full-window MODAL overlay at the in-game modal z-layer (20). The canvas
    // DialogForm is kfFrmAutoTakedown, so a tap OUTSIDE the dialog dismisses it (= NO /
    // kidcCancel). pointer-events:all so the click reaches us (and the canvas gets none).
    s.position = 'fixed';
    s.inset = '0';
    s.zIndex = '20';
    s.color = COL_FG;
    s.fontFamily = FONT;
    s.userSelect = 'none';
    s.pointerEvents = 'all';
    s.overflow = 'hidden';
    d.root.addEventListener('pointerdown', this.onBackdrop);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('keydown', this.onKeyDown);
    this.render();
  }

  private readonly onResize = (): void => {
    this.render();
  };

  // a tap NOT on the dialog → NO (AutoTakedown, form default kidcCancel).
  private readonly onBackdrop = (e: PointerEvent): void => {
    const stage = this.d.root.querySelector('[data-confirm-stage]');
    if (stage !== null && e.target instanceof Node && stage.contains(e.target)) return;
    this.resolve(false);
  };

  // Esc → NO (kidcCancel — the kidfAreYouSure FORM default).
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.resolve(false);
    }
  };

  destroy(): void {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onKeyDown);
    this.d.root.removeEventListener('pointerdown', this.onBackdrop);
  }

  /** report the choice ONCE (the host unmounts + routes to the continuation). */
  private resolve(confirmed: boolean): void {
    if (this.resolved) return;
    this.resolved = true;
    this.d.onResult(confirmed);
  }

  // ── the faithful design stage (forms.ini: the confirmation form is 73×45) ──────
  // The SAME fixed magnification family as the other in-game DOM forms (chrome.ts
  // formScale / at), at the confirmation form's own 73×45 size.
  render(): void {
    const s = formScale();
    const stageW = FORM.w * s,
      stageH = FORM.h * s;

    // the stage = the DialogForm: a BLACK frame (gcxyBorder), inside it a side-colour
    // TITLE BAR (top, kcyTitle tall) and a TRANSLUCENT body (the gob shows through).
    const stage = el('div', {
      position: 'absolute',
      width: `${stageW}px`,
      height: `${stageH}px`,
      backgroundColor: '#000', // the kiclrBlack DrawBorder frame (shows at the edges)
      boxSizing: 'border-box',
    });
    stage.dataset.confirmStage = '1';

    // the TRANSLUCENT body (kiclrShadow / ShadowHelper) — below the title bar, inset by
    // the border. The gob behind shows through, darkened.
    stage.append(
      el('div', {
        position: 'absolute',
        left: `${BORDER * s}px`,
        top: `${CY_TITLE * s}px`,
        width: `${(FORM.w - 2 * BORDER) * s}px`,
        height: `${(FORM.h - CY_TITLE - BORDER) * s}px`,
        background: 'rgba(0, 0, 0, 0.5)',
      }),
    );

    // the TITLE BAR — side colour (SetTitleColor), from the top border to kcyTitle.
    stage.append(
      el('div', {
        position: 'absolute',
        left: `${BORDER * s}px`,
        top: `${BORDER * s}px`,
        width: `${(FORM.w - 2 * BORDER) * s}px`,
        height: `${(CY_TITLE - BORDER) * s}px`,
        background: this.d.titleColor ?? 'rgb(40, 86, 150)',
      }),
    );

    // kidcTitle (0,2,73,0) center kifntTitle — the runtime title, WHITE on the colour bar.
    const title = formTitle(this.d.title, s, {
      fontPx: Math.round(6 * s),
      color: '#fff',
      textShadow: '0 1px 2px rgba(0,0,0,0.65)',
    });
    title.dataset.confirmTitle = '1';
    stage.append(at(title, s, TITLE.x, TITLE.y, TITLE.w));

    // the static "Are you sure?" prompt (12,15,55,0) center kifntShadow — in the body.
    // The Eurostile web face is wider than the canvas bitmap font, so the nominal 55-unit
    // box clipped ("Are you sur…"). The canvas centers the label on the form, so span the
    // FULL inner width (centered) and don't clip — the text reads fully like the canvas.
    const prompt = this.label('Are you sure?', s);
    prompt.style.overflow = 'visible';
    prompt.dataset.confirmPrompt = '1';
    stage.append(at(prompt, s, BORDER, PROMPT.y, FORM.w - 2 * BORDER));

    // kidcOk "Yes" → onResult(true); kidcCancel "No" → false. Each button is SIZED to its
    // label via the shared buttonWidthU (minW=24 for the small dialog buttons) then the pair
    // is CENTRED as a row on the 73-wide form with an even gap, so the WI-font label keeps
    // clear margin inside the 9-slice frame (the fixed 29/27u boxes cramped them).
    const wYes = buttonWidthU('Yes', YES.h, s, 24);
    const wNo = buttonWidthU('No', NO.h, s, 24);
    const gapU = 5;
    let bx = Math.max(BORDER, (FORM.w - (wYes + wNo + gapU)) / 2);
    const yes = this.btn('Yes', () => this.resolve(true), { w: wYes * s, h: YES.h * s });
    yes.dataset.confirmYes = '1';
    stage.append(at(yes, s, bx, YES.y));
    bx += wYes + gapU;
    const no = this.btn('No', () => this.resolve(false), { w: wNo * s, h: NO.h * s });
    no.dataset.confirmNo = '1';
    stage.append(at(no, s, bx, NO.y));

    // POSITION the stage OVER THE GOB (centred on the gob screen centre, clamped to the
    // playfield box — the root's box: the window in the shipped client, the play pane in
    // the mission editor), like the canvas PopupConfirmation; the forceShow preview (no
    // anchor) falls back to playfield-centre. Root coords ARE playfield coords (rootBox).
    const magnify = this.d.magnify ?? 1;
    const vp = rootBox(this.d.root);
    let left = (vp.width - stageW) / 2,
      top = (vp.height - stageH) / 2;
    if (this.d.anchor != null) {
      left = this.d.anchor.cx * magnify - stageW / 2;
      top = this.d.anchor.cy * magnify - stageH / 2;
    }
    stage.style.left = `${Math.round(Math.max(0, Math.min(left, vp.width - stageW)))}px`;
    stage.style.top = `${Math.round(Math.max(0, Math.min(top, vp.height - stageH)))}px`;

    this.d.root.replaceChildren(stage);
  }

  // ── widgets ────────────────────────────────────────────────────────────────────
  // the "Are you sure?" label (kifntShadow) — in the translucent body. (The title +
  // the 9-slice button are the chrome.ts primitives — see formTitle/makeStageButton.)
  private label(text: string, s: number): HTMLElement {
    const e = el('div', {
      fontFamily: FONT,
      fontSize: `${Math.round(6 * s)}px`,
      color: COL_FG,
      textShadow: shadow,
      textAlign: 'center',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    });
    e.textContent = text;
    return e;
  }
}
